import { Controller } from "@hotwired/stimulus"
import { planningMutation } from "../planning_mutation"

// The "Planung" dialog used to assign a resource across a time span. It opens via the
// `planning:plan-range` CustomEvent in one of two modes:
//   - "fixed": a resource was dropped onto a cell — the resource is shown read-only.
//   - "pick":  a cell was clicked — the dialog shows a Mitarbeiter/Gewerk toggle + a searchable
//              picker so the user chooses the resource (Mitarbeiter and whole Teams combined).
//   - "plannablePick": the employee-centric layout inverts this — the Baustelle/Aufgabe is picked
//              here (or was dropped, in which case it is pre-selected), and the combined
//              Mitarbeiter/Teams picker starts with the clicked row's employee, so the same span
//              can be planned for more people at once. Combined with `editMode` it edits an existing run
//              ("Baustelle ändern", every layout): the picker is preset to the run's plannable;
//              keeping it reconciles the dates (set_span), picking another moves the run
//              there (retarget_span). In the employee layout the run edit also offers
//              "Baustelle beauftragen …" (saveAndDispatch), which replaces this dialog with the
//              site's "Plan beauftragen" — saving the edit first when anything changed.
// In both modes the von..bis is pre-filled to the cell (a single day, or the period for an
// aggregated cell) and adjustable, and saving POSTs to the matching range endpoint.
export default class extends Controller {
  static targets = [
    "root",
    "title",
    "resourceRow",
    "resource",
    "plannable",
    "summaryList",
    "info",
    "dateSection",
    "from",
    "to",
    "workingDaysOnly",
    "count",
    "countRow",
    "labelRow",
    "label",
    "pickSection",
    "plannableSection",
    "plannableSelect",
    "employeePicker",
    "tradePicker",
    "fleetPicker",
    "employeeSelect",
    "tradeSelect",
    "fleetSelect",
    "typeEmployeeBtn",
    "typeTradeBtn",
    "typeFleetBtn",
    "submit",
    "spinner",
    "pickError",
    "blockerSwitch",
    "dispatchButton",
    "dispatchLabel",
    "panel",
  ]
  static values = {
    allocationUrl: String,
    setSpanUrl: String,
    retargetUrl: String,
    relabelUrl: String,
    infoRetarget: String,
    tradeDemandUrl: String,
    fleetUrl: String,
    fleetCrewUrl: String,
    titleEmployee: String,
    titleTrade: String,
    titleFleet: String,
    titleFleetCrew: String,
    titleTeam: String,
    titleEdit: String,
    infoEmployee: String,
    infoTrade: String,
    infoFleet: String,
    infoFleetCrew: String,
    infoTeam: String,
    // Several people (or a team) picked in the combined Mitarbeiter/Teams picker.
    infoPeople: String,
    // Noun shown after the count in the resource row for a multi-select drop ("N Mitarbeiter").
    resourceMultiple: String,
    // The planner's remembered "Nur Arbeitstage planen" choice (stored per account on the server).
    workingDaysDefault: { type: Boolean, default: true },
    // dispatches#new (naming the version on screen); only rendered where the board may dispatch.
    dispatchUrl: String,
    dispatchLabel: String,
    saveAndDispatchLabel: String,
  }

  connect() {
    this.current = null
    this.mode = "fixed"
    this.type = "employee"
    this.boundOpen = this.open.bind(this)
    this.boundKeydown = this.onKeydown.bind(this)
    document.addEventListener("planning:plan-range", this.boundOpen)
  }

  disconnect() {
    document.removeEventListener("planning:plan-range", this.boundOpen)
    document.removeEventListener("keydown", this.boundKeydown)
  }

  open(event) {
    const detail = event.detail
    this.current = detail
    this.hidePickError()
    this.mode = detail.mode || (detail.resourceId ? "fixed" : "pick")
    // Multi-select drop: several employees planned across the same span (employee-only; never
    // trade or edit mode). The resource row shows "N Mitarbeiter" and save posts employee_ids.
    this.resourceIds = detail.resourceIds?.length > 1 ? detail.resourceIds : null
    // The dragged resource kind in fixed mode ("Employee" | "Trade" | "Fleet" | "FleetCrew");
    // decides which range endpoint a save posts to.
    this.resourceType = detail.resourceType
    // Combined vehicle+driver drag: carry both ids so save can plan the pair across the span.
    this.fleetId = detail.fleetId
    this.employeeId = detail.employeeId
    // Edit mode (timeline): a PATCH carrying the run's original extent. Two variants:
    //   - set_span (employee bar): reconcile one run to a new range.
    //   - set_count (trade lane): change the whole lane's required_count only (Zeitraum hidden).
    this.editContext = detail.editMode
      ? {
          url: detail.editCount ? detail.setCountUrl : detail.setSpanUrl || this.setSpanUrlValue,
          countOnly: Boolean(detail.editCount),
          originalFrom: detail.originalFrom,
          originalTo: detail.originalTo,
          allocationId: detail.allocationId,
        }
      : null

    this.plannableTarget.textContent = detail.plannableName || "—"
    if (this.hasBlockerSwitchTarget) this.toggle(this.blockerSwitchTarget, false)
    this.toggleEmployeePicker(false)
    this.toggleSummaryList(true)
    this.setDate(this.fromTarget, detail.periodStart)
    this.setDate(this.toTarget, detail.periodEnd)
    // An explicit caller value wins (the whole-site-duration drop). A single picked day stays
    // unchecked — on a Saturday or holiday the range would otherwise hold no working day at all.
    // Every other open starts from the planner's remembered choice.
    const singleDay = Boolean(detail.periodStart) && detail.periodStart === detail.periodEnd
    this.workingDaysOnlyTarget.checked =
      detail.workingDaysOnly ?? (singleDay ? false : this.workingDaysDefaultValue)
    this.workingDaysChanged = false
    // A run edit opens on the run's current label; every create starts blank.
    this.originalLabel = detail.label || ""
    if (this.hasLabelTarget) this.labelTarget.value = this.originalLabel

    if (this.mode === "plannablePick") {
      // Employee fixed (the row), target chosen here. A drop pre-selects the dropped plannable.
      this.pickSectionTarget.classList.add("hidden")
      this.togglePlannableSection(true)
      this.togglePlannableRow(false)
      this.presetPlannable(detail)
      this.applyType("employee")
      if (this.editContext) {
        // Editing one person's run stays single-employee: set_span / retarget_span move exactly it.
        this.toggle(this.resourceRowTarget, true)
        this.resourceTarget.textContent = detail.employeeName || "—"
      } else {
        // Creating: the clicked row's employee is a pre-selected, removable pick among many.
        if (this.hasBlockerSwitchTarget) this.toggle(this.blockerSwitchTarget, true)
        this.toggle(this.resourceRowTarget, false)
        this.toggleSummaryList(false)
        this.toggleEmployeePicker(true)
        this.presetPeople(detail.employeeId, detail.employeeName)
      }
    } else if (this.mode === "pick") {
      this.togglePlannableSection(false)
      this.togglePlannableRow(true)
      this.pickSectionTarget.classList.remove("hidden")
      this.toggle(this.resourceRowTarget, false)
      this.resetSelect(this.employeeSelectTarget)
      this.resetSelect(this.tradeSelectTarget)
      if (this.hasFleetSelectTarget) this.resetSelect(this.fleetSelectTarget)
      this.setType("employee")
    } else {
      this.togglePlannableSection(false)
      this.togglePlannableRow(true)
      this.pickSectionTarget.classList.add("hidden")
      this.toggle(this.resourceRowTarget, true)
      this.resourceTarget.textContent = this.resourceIds
        ? `${this.resourceIds.length} ${this.resourceMultipleValue}`.trim()
        : detail.resourceName || "—"
      this.applyType(this.fixedTypeFor(detail.resourceType))
      if (detail.resourceType === "FleetCrew") this.applyFleetCrewLabels()
      if (detail.resourceType === "Team") this.applyTeamLabels()
      // Trade edit (timeline bar): prefill the Anzahl field with the run's current count.
      if (detail.requiredCount != null) this.countTarget.value = detail.requiredCount
    }

    // A count-only edit (trade lane) hides the Zeitraum section — only the Anzahl is editable; every
    // other open shows it.
    this.toggleDateSection(!this.editContext?.countOnly)

    // Edit mode relabels the header to the generic "edit" title.
    if (this.editContext && this.titleEditValue) this.titleTarget.textContent = this.titleEditValue
    if (this.editContext && this.mode === "plannablePick" && this.infoRetargetValue)
      this.infoTarget.textContent = this.infoRetargetValue
    this.toggleDispatchButton()

    this.rootTarget.classList.remove("hidden")
    this.rootTarget.classList.add("flex")
    document.addEventListener("keydown", this.boundKeydown)
  }

  // The planner changed the box themselves — remember this choice on save (see workingDaysParams).
  workingDaysTouched() {
    this.workingDaysChanged = true
  }

  // Always sent explicitly, so an unticked box is recorded too. Only remembered when the planner
  // touched the box: an untouched box shows either the stored preference already or a value forced
  // by the caller (the whole-site-duration drop, the single-day "off"), and recording a forced value
  // would overwrite the planner's actual choice.
  workingDaysParams() {
    const params = { working_days_only: this.workingDaysOnlyTarget.checked ? "1" : "0" }
    if (this.workingDaysChanged) {
      params.remember_working_days = "1"
      this.workingDaysDefaultValue = this.workingDaysOnlyTarget.checked
    }
    return params
  }

  // Pick-mode toggle handlers.
  selectEmployee() {
    this.setType("employee")
  }

  selectTrade() {
    this.setType("trade")
  }

  selectFleet() {
    this.setType("fleet")
  }

  setType(type) {
    this.type = type
    this.toggleEmployeePicker(type === "employee")
    this.tradePickerTarget.classList.toggle("hidden", type !== "trade")
    if (this.hasFleetPickerTarget)
      this.fleetPickerTarget.classList.toggle("hidden", type !== "fleet")
    this.styleToggleButton(this.typeEmployeeBtnTarget, type === "employee")
    this.styleToggleButton(this.typeTradeBtnTarget, type === "trade")
    if (this.hasTypeFleetBtnTarget)
      this.styleToggleButton(this.typeFleetBtnTarget, type === "fleet")
    this.applyType(type)
    if (type === "employee") this.peopleChanged()
  }

  // Maps a dragged resourceType to the dialog's internal type key.
  fixedTypeFor(resourceType) {
    if (resourceType === "Trade") return "trade"
    if (resourceType === "Fleet") return "fleet"
    return "employee"
  }

  // Title / explanation / Anzahl field, shared by both modes.
  applyType(type) {
    const isTrade = type === "trade"
    const isFleet = type === "fleet"
    this.titleTarget.textContent = isTrade
      ? this.titleTradeValue
      : isFleet
        ? this.titleFleetValue
        : this.titleEmployeeValue
    this.infoTarget.textContent = isTrade
      ? this.infoTradeValue
      : isFleet
        ? this.infoFleetValue
        : this.infoEmployeeValue
    // The Anzahl field is trade-only (a rough demand count); employees and fleets are concrete.
    this.countRowTarget.classList.toggle("hidden", !isTrade)
    this.countRowTarget.classList.toggle("flex", isTrade)
    this.toggleLabelRow(!isTrade && !isFleet)
  }

  // The Bezeichnung field belongs to employee allocations only (people, teams, run edits).
  toggleLabelRow(visible) {
    if (!this.hasLabelRowTarget) return
    this.labelRowTarget.classList.toggle("hidden", !visible)
    this.labelRowTarget.classList.toggle("flex", visible)
  }

  // Sent only while the field is shown, so a trade or fleet save never carries a stale label.
  labelParams() {
    if (!this.hasLabelRowTarget || this.labelRowTarget.classList.contains("hidden")) return {}
    return { label: this.labelTarget.value.trim() }
  }

  // A run edit posts the Bezeichnung only when the planner changed it. The field opens on the
  // run's display label, which for a run whose segments carry several labels is the joined
  // "A · B" — posting it back untouched would write that joined text onto every day.
  changedLabelParams() {
    const params = this.labelParams()
    return params.label === undefined || params.label === this.originalLabel ? {} : params
  }

  // FleetCrew is drag-only (never a pick-mode type), so it has no internal toggle key — relabel the
  // header/explanation directly and hide the trade-only Anzahl row.
  applyFleetCrewLabels() {
    this.titleTarget.textContent = this.titleFleetCrewValue
    this.infoTarget.textContent = this.infoFleetCrewValue
    this.countRowTarget.classList.add("hidden")
    this.countRowTarget.classList.remove("flex")
    this.toggleLabelRow(false)
  }

  // Team is drag-only too: same relabel treatment as FleetCrew.
  applyTeamLabels() {
    this.titleTarget.textContent = this.titleTeamValue
    this.infoTarget.textContent = this.infoTeamValue
    this.countRowTarget.classList.add("hidden")
    this.countRowTarget.classList.remove("flex")
  }

  // The combined picker's explanation: the singular text for one person, the plural one as soon
  // as several people or any team are picked. Edit mode keeps its own retarget text.
  peopleChanged() {
    if (this.editContext || this.type !== "employee") return
    // A pick answers the "nothing selected" error of an earlier save attempt.
    if (this.peopleParams()) this.hidePickError()
    const { ids, teamIds } = this.pickedPeople()
    const several = teamIds.length > 0 || ids.length > 1
    this.infoTarget.textContent = several ? this.infoPeopleValue : this.infoEmployeeValue
  }

  toggleEmployeePicker(visible) {
    this.employeePickerTarget.classList.toggle("hidden", !visible)
  }

  toggleSummaryList(visible) {
    if (this.hasSummaryListTarget) this.summaryListTarget.classList.toggle("hidden", !visible)
  }

  // Start the combined picker with just the clicked row's employee. The rendered list is the
  // board's row pool, but a row can exist for someone outside it (e.g. after a filter change
  // without reload) — add the missing option rather than silently starting empty.
  presetPeople(employeeId, employeeName) {
    const select = this.employeeSelectTarget
    const controller = this.application.getControllerForElementAndIdentifier(select, "select")
    if (controller && typeof controller.resetOptions === "function") controller.resetOptions()
    const value = employeeId ? `Employee-${employeeId}` : null
    if (value && ![...select.options].some((option) => option.value === value)) {
      select.add(new Option(employeeName || "—", value))
      if (controller && typeof controller.refresh === "function") controller.refresh()
    }
    const values = value ? [value] : []
    if (controller && typeof controller.setSelected === "function") {
      controller.setSelected(values)
    } else {
      ;[...select.options].forEach((option) => (option.selected = values.includes(option.value)))
    }
    this.peopleChanged()
  }

  // The read-only "Ziel" row, hidden when the picker above already names the target.
  togglePlannableRow(visible) {
    this.plannableTarget.closest("div")?.classList.toggle("hidden", !visible)
  }

  togglePlannableSection(visible) {
    if (this.hasPlannableSectionTarget)
      this.plannableSectionTarget.classList.toggle("hidden", !visible)
  }

  // Pre-select the dropped Baustelle/Aufgabe (drop onto an aggregated cell), or clear the picker
  // so a plain cell click starts empty.
  presetPlannable(detail) {
    if (!this.hasPlannableSelectTarget) return
    const key =
      detail.plannableType && detail.plannableId
        ? `${detail.plannableType}-${detail.plannableId}`
        : ""
    const controller = this.application.getControllerForElementAndIdentifier(
      this.plannableSelectTarget,
      "select",
    )
    // A search in the previous open replaced the picker's options with its hits; start every
    // open from the rendered default list again.
    if (controller && typeof controller.resetOptions === "function") controller.resetOptions()
    if (!key) {
      if (controller && typeof controller.clear === "function") controller.clear()
      else this.plannableSelectTarget.value = ""
      return
    }
    this.ensurePlannableOption(key, detail.plannableName)
    this.plannableSelectTarget.value = key
    if (controller && typeof controller.refresh === "function") controller.refresh()
  }

  // The picker renders the DEFAULT (capped) list, while the panel's search reaches EVERY
  // Baustelle — so a card found past the cap (or a run's plannable in "Baustelle ändern") may
  // have no option here and `select.value = key` would silently fall back to "", leaving the
  // save to fail with "nichts ausgewählt". Add the missing option from the payload; the picker's
  // own search finds everything else.
  ensurePlannableOption(key, name) {
    const select = this.plannableSelectTarget
    if ([...select.options].some((option) => option.value === key)) return

    select.add(new Option(name || key, key))
  }

  // The chosen target as { plannable_type, plannable_id }, or null when nothing is picked.
  pickedPlannable() {
    const key = this.hasPlannableSelectTarget ? this.plannableSelectTarget.value : ""
    if (!key) return null
    const [type, id] = key.split("-")
    return { plannable_type: type, plannable_id: Number(id) }
  }

  styleToggleButton(button, active) {
    button.classList.toggle("bg-green-600", active)
    button.classList.toggle("text-white", active)
    button.classList.toggle("text-gray-600", !active)
  }

  // Employee layout: the clicked row and the chosen days, as a capacity blocker instead
  // (planning-blocker-dialog). Hands back the days as currently entered here.
  switchToBlocker() {
    if (!this.current) return
    const detail = {
      employeeId: this.current.employeeId,
      employeeName: this.current.employeeName,
      periodStart: this.fromTarget.value || this.current.periodStart,
      periodEnd: this.toTarget.value || this.current.periodEnd,
    }
    this.close()
    document.dispatchEvent(new CustomEvent("planning:plan-blocker", { detail }))
  }

  // "Baustelle beauftragen …" belongs to an employee run edit only — never a create or a trade lane.
  toggleDispatchButton() {
    if (!this.hasDispatchButtonTarget) return
    const visible = Boolean(
      this.dispatchUrlValue && this.editContext && this.mode === "plannablePick",
    )
    this.toggle(this.dispatchButtonTarget, visible)
    // Wider only then, so "Speichern und beauftragen …" fits beside Abbrechen/Speichern.
    if (this.hasPanelTarget) {
      this.panelTarget.classList.toggle("max-w-xl", visible)
      this.panelTarget.classList.toggle("max-w-lg", !visible)
    }
    if (visible) this.refreshDispatchLabel()
  }

  // Any edit in the dialog renames the button, so the planner knows the click saves first (AC3).
  refreshDispatchLabel() {
    if (!this.hasDispatchLabelTarget || !this.current || !this.editContext) return
    this.dispatchLabelTarget.textContent = this.runEditChanged()
      ? this.saveAndDispatchLabelValue
      : this.dispatchLabelValue
  }

  // Opens "Plan beauftragen" for the run's site in place of this dialog. An edited run is saved
  // first, and only a successful save opens it — on failure this dialog stays open with the error,
  // as after "Speichern". The dialog then opens for the plannable just saved: a run moved to
  // another Baustelle/Aufgabe is dispatched where it now is.
  async saveAndDispatch() {
    if (!this.current || !this.editContext) return
    this.hidePickError()
    const plannable = this.pickedPlannable()
    if (!plannable) return this.showPickError()

    // Cleared by dismiss(): cancelling while the save or the request is in flight drops the
    // Beauftragung (the save itself is already on its way and still lands).
    this.dispatchRequested = true
    if (this.runEditChanged() && !(await this.saveRunEdit(plannable))) return
    if (this.dispatchRequested) await this.openDispatch(plannable)
  }

  // dispatches#new resolves a task to its Baustelle, so a run on an Aufgabe needs no site id here.
  // planningMutation renders the dialog's stream — or the refusal's flash (a version, a hidden
  // site). An unchanged run's dialog stays open (pending) until then, so a refusal leaves the
  // planner in "Einsatz bearbeiten" rather than with no dialog at all.
  async openDispatch({ plannable_type: type, plannable_id: id }) {
    const url = new URL(this.dispatchUrlValue, window.location.origin)
    url.searchParams.set(type === "Task" ? "task_id" : "site_id", id)
    this.setPending(true)
    const { ok } = await planningMutation({
      url: url.toString(),
      method: "GET",
      component: "planning-allocation-dialog",
    })
    if (!this.dispatchRequested) {
      // Dismissed meanwhile: empty the container as dispatches#create does to close the dialog.
      if (ok) document.getElementById("planning_dispatch_dialog")?.replaceChildren()
    } else if (ok && this.current) {
      this.close()
    } else {
      this.setPending(false)
    }
    this.dispatchRequested = false
  }

  // Abbrechen, Escape or the backdrop: the planner walked away, so a pending
  // "Speichern und beauftragen …" must not open "Plan beauftragen" afterwards.
  dismiss() {
    this.dispatchRequested = false
    this.close()
  }

  close() {
    this.rootTarget.classList.add("hidden")
    this.rootTarget.classList.remove("flex")
    this.current = null
    this.setPending(false)
    document.removeEventListener("keydown", this.boundKeydown)
    // The employee board keeps its drag-select ghost on screen until here.
    document.dispatchEvent(new CustomEvent("planning:plan-range-closed"))
  }

  // Disable the Save button + show its spinner while a save is in flight, so the dialog can't be
  // submitted twice and the user sees the action is processing (it stays open until the server
  // replies — see post()).
  setPending(pending) {
    if (this.hasSubmitTarget) this.submitTarget.disabled = pending
    if (this.hasDispatchButtonTarget) this.dispatchButtonTarget.disabled = pending
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.toggle("hidden", !pending)
  }

  // Escape closes the dialog, but only when it is the topmost layer: with a date field's
  // calendar popover open, that keystroke belongs to the calendar. The picker marks the event
  // handled (preventDefault) when it dismisses its own panel; the :popover-open check also
  // covers the native light-dismiss, which closes the popover without touching the event.
  onKeydown(event) {
    if (event.key !== "Escape") return
    if (event.defaultPrevented || this.rootTarget.querySelector(":popover-open")) return

    this.dismiss()
  }

  // Where the press STARTED, so a click is only a backdrop click when both ends of it are on
  // the backdrop. Selecting the text of a date field by dragging routinely slips past the
  // panel's edge (the Von field sits ~24px from it), and the resulting click — whose target is
  // the nearest common ancestor, i.e. the backdrop — used to close the dialog and discard
  // everything the user had entered.
  backdropPress(event) {
    this.pressedOnBackdrop = event.target === this.rootTarget
  }

  backdrop(event) {
    const startedOnBackdrop = this.pressedOnBackdrop
    this.pressedOnBackdrop = false
    if (event.target === this.rootTarget && startedOnBackdrop) this.dismiss()
  }

  // A save without a picked resource/target must say so — a dialog that just refuses to
  // close reads as broken.
  showPickError() {
    if (this.hasPickErrorTarget) this.pickErrorTarget.classList.remove("hidden")
  }

  hidePickError() {
    if (this.hasPickErrorTarget) this.pickErrorTarget.classList.add("hidden")
  }

  save() {
    if (!this.current) return
    const detail = this.current
    this.hidePickError()

    // Employee layout: the employee is fixed by the row; the target comes from the picker. Posts
    // to the same range endpoint as every other span create.
    if (this.mode === "plannablePick") {
      const plannable = this.pickedPlannable()
      if (!plannable) return this.showPickError()
      if (this.editContext) return this.saveRunEdit(plannable)
      const people = this.peopleParams()
      if (!people) return this.showPickError()
      const body = {
        ...people,
        ...plannable,
        date_from: this.fromTarget.value,
        date_to: this.toTarget.value,
        ...this.workingDaysParams(),
        ...this.labelParams(),
      }
      this.post(this.allocationUrlValue, body)
      return
    }

    const context = {
      plannable_type: detail.plannableType,
      plannable_id: Number(detail.plannableId),
      date_from: this.fromTarget.value,
      date_to: this.toTarget.value,
      ...(this.editContext?.countOnly ? {} : this.workingDaysParams()),
    }

    // Multi-select drop (fixed mode only): create the span for every selected resource at once.
    if (this.resourceIds) {
      if (this.resourceType === "Fleet") {
        this.post(this.fleetUrlValue, { fleet_ids: this.resourceIds, ...context })
      } else {
        this.post(this.allocationUrlValue, {
          employee_ids: this.resourceIds,
          ...context,
          ...this.labelParams(),
        })
      }
      return
    }

    // Pick mode: employees and fleets are multi-select (a trade is a single demand + count).
    // Always post the array (`_ids`) form — the range endpoints accept a one-element array too.
    if (this.mode === "pick") {
      const picked = this.pickedSelection()
      if (!picked) return this.showPickError()
      if (picked.type === "Trade") {
        const count = Number(this.countTarget.value) || 1
        this.post(this.tradeDemandUrlValue, {
          trade_id: picked.ids[0],
          required_count: count,
          ...context,
        })
      } else if (picked.type === "Fleet") {
        this.post(this.fleetUrlValue, { fleet_ids: picked.ids, ...context })
      } else {
        this.post(this.allocationUrlValue, {
          ...this.peopleParams(),
          ...context,
          ...this.labelParams(),
        })
      }
      return
    }

    const resource = this.fixedResource(detail)
    if (!resource) return this.showPickError()

    // Count-only edit (trade lane): set the whole lane's required_count via set_count. The Zeitraum
    // is hidden but its from/to still carry the lane's extent (the range of demands to update).
    if (this.editContext?.countOnly) {
      this.post(
        this.editContext.url,
        {
          trade_id: resource.id,
          required_count: Number(this.countTarget.value) || 1,
          plannable_type: detail.plannableType,
          plannable_id: Number(detail.plannableId),
          date_from: this.fromTarget.value,
          date_to: this.toTarget.value,
        },
        "PATCH",
      )
      return
    }

    // Edit mode: reconcile the existing run via set_span (PATCH), carrying its original extent.
    if (this.editContext) {
      this.post(
        this.editContext.url,
        {
          employee_id: resource.id,
          ...context,
          original_from: this.editContext.originalFrom,
          original_to: this.editContext.originalTo,
          ...this.changedLabelParams(),
        },
        "PATCH",
      )
      return
    }

    if (resource.type === "FleetCrew") {
      this.post(this.fleetCrewUrlValue, {
        fleet_id: this.fleetId,
        employee_id: this.employeeId,
        ...context,
      })
    } else if (resource.type === "Trade") {
      const count = Number(this.countTarget.value) || 1
      this.post(this.tradeDemandUrlValue, {
        trade_id: resource.id,
        required_count: count,
        ...context,
      })
    } else if (resource.type === "Fleet") {
      this.post(this.fleetUrlValue, { fleet_id: resource.id, ...context })
    } else if (resource.type === "Team") {
      // Whole-team span: the server resolves the members and plans each individually.
      this.post(this.allocationUrlValue, {
        team_id: resource.id,
        ...context,
        ...this.labelParams(),
      })
    } else {
      this.post(this.allocationUrlValue, {
        employee_id: resource.id,
        ...context,
        ...this.labelParams(),
      })
    }
  }

  // "Einsatz bearbeiten" of an employee run: the same plannable only reconciles the Zeitraum
  // (set_span); another one moves the run there (retarget_span). Both carry the run's original
  // extent so exactly this run changes. Saving with nothing changed is a no-op: set_span would
  // re-plan the run's range under the Arbeitstage box — dropping deliberately planned weekend or
  // holiday days when ticked, filling the weekends between the run's weeks when not.
  // A changed Bezeichnung alone goes to relabel_span instead, which touches no date — for the
  // same reason.
  // Resolves to whether the save went through (an unchanged edit just closes: true).
  saveRunEdit(plannable) {
    if (!this.runEditChanged()) {
      this.close()
      return Promise.resolve(true)
    }
    const detail = this.current
    const unchanged = this.samePlannable(plannable)
    // Neither plannable nor dates changed, so only the Bezeichnung did.
    if (unchanged && !this.workingDaysChanged && this.rangeUnchanged()) {
      return this.post(
        this.relabelUrlValue,
        {
          employee_id: detail.employeeId,
          plannable_type: detail.plannableType,
          plannable_id: Number(detail.plannableId),
          original_from: this.editContext.originalFrom,
          original_to: this.editContext.originalTo,
          // A grid chip is one row of its day; the server relabels only that row.
          ...(this.editContext.allocationId
            ? { allocation_id: this.editContext.allocationId }
            : {}),
          ...this.changedLabelParams(),
        },
        "PATCH",
      )
    }

    const body = {
      employee_id: detail.employeeId,
      plannable_type: detail.plannableType,
      plannable_id: Number(detail.plannableId),
      date_from: this.fromTarget.value,
      date_to: this.toTarget.value,
      original_from: this.editContext.originalFrom,
      original_to: this.editContext.originalTo,
      ...this.workingDaysParams(),
      ...this.changedLabelParams(),
    }
    if (unchanged) return this.post(this.editContext.url, body, "PATCH")

    return this.post(
      this.retargetUrlValue,
      {
        ...body,
        target_plannable_type: plannable.plannable_type,
        target_plannable_id: plannable.plannable_id,
      },
      "PATCH",
    )
  }

  // Whether saving this run edit would write anything — saveRunEdit's no-op test and the
  // Beauftragen button's label. A cleared picker counts as a change: the save refuses it with the
  // pick error.
  runEditChanged() {
    const plannable = this.pickedPlannable()
    return (
      !plannable ||
      !this.samePlannable(plannable) ||
      this.workingDaysChanged ||
      !this.rangeUnchanged() ||
      this.changedLabelParams().label !== undefined
    )
  }

  // The picked Baustelle/Aufgabe is still the run's own.
  samePlannable(plannable) {
    return (
      plannable.plannable_type === this.current.plannableType &&
      plannable.plannable_id === Number(this.current.plannableId)
    )
  }

  // The edited von..bis still equals the run's original extent (either order, as the server reads it).
  rangeUnchanged() {
    const range = [this.fromTarget.value, this.toTarget.value].sort()
    const original = [this.editContext.originalFrom, this.editContext.originalTo].sort()
    return range[0] === original[0] && range[1] === original[1]
  }

  // Pick-mode selection. Trade is single; employee and fleet are multi-select, so collect every
  // ticked option id. Returns null when nothing is selected.
  pickedSelection() {
    if (this.type === "trade") {
      const id = this.tradeSelectTarget.value
      return id ? { type: "Trade", ids: [id] } : null
    }
    if (this.type === "fleet") {
      const ids = this.selectedIds(this.fleetSelectTarget)
      return ids.length ? { type: "Fleet", ids } : null
    }
    return this.peopleParams() ? { type: "Employee" } : null
  }

  // The combined picker's values ("Employee-34", "Team-12") split by kind.
  pickedPeople() {
    const ids = []
    const teamIds = []
    this.selectedIds(this.employeeSelectTarget).forEach((value) => {
      const [kind, id] = value.split("-")
      if (kind === "Team") teamIds.push(Number(id))
      else if (kind === "Employee") ids.push(Number(id))
    })
    return { ids, teamIds }
  }

  // The range endpoint's params for the picked people, or null when nothing is picked. The
  // server merges both lists and plans every person once.
  peopleParams() {
    const { ids, teamIds } = this.pickedPeople()
    if (!ids.length && !teamIds.length) return null
    const params = {}
    if (ids.length) params.employee_ids = ids
    if (teamIds.length) params.team_ids = teamIds
    return params
  }

  selectedIds(select) {
    return Array.from(select.selectedOptions)
      .map((option) => option.value)
      .filter(Boolean)
  }

  fixedResource(detail) {
    return detail.resourceId ? { type: detail.resourceType, id: detail.resourceId } : null
  }

  // Common::DateFieldComponent keeps a hidden ISO value next to the visible dd.mm.yyyy input and
  // only mirrors one into the other on `change`. Writing .value alone left the field looking
  // empty while posting the right date, so dispatch the event the picker listens for.
  setDate(target, iso) {
    target.value = iso || ""
    target.dispatchEvent(new Event("change", { bubbles: true }))
  }

  // Show/hide a flex row without the hidden/flex display conflict.
  toggle(el, visible) {
    el.classList.toggle("hidden", !visible)
    el.classList.toggle("flex", visible)
  }

  // Show/hide the whole Zeitraum (von/bis + Arbeitstage) section — hidden for a count-only edit.
  toggleDateSection(visible) {
    if (this.hasDateSectionTarget) this.dateSectionTarget.classList.toggle("hidden", !visible)
  }

  resetSelect(el) {
    const controller = this.application.getControllerForElementAndIdentifier(el, "select")
    if (controller && typeof controller.clear === "function") {
      controller.clear()
    } else {
      el.value = ""
    }
  }

  async post(url, body, method = "POST") {
    this.setPending(true)
    const { ok } = await planningMutation({
      url,
      method,
      body,
      component: "planning-allocation-dialog",
    })
    // Close only on success; on failure keep the dialog open (with an error toast) so the user can
    // adjust and retry instead of losing their input.
    if (ok) {
      // A multi-select span was planned — drop the panel's ticked set so it doesn't linger.
      if (this.resourceIds) document.dispatchEvent(new CustomEvent("planning:clear-selection"))
      this.close()
    } else {
      this.setPending(false)
    }
    return ok
  }
}
